import useCreateLog from '@/log/hooks/useCreateLog'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { RoleFragment } from '@gql'
import { EntityChangeType, LogType } from '@rolebase/shared/model/log'
import { useCallback } from 'react'
import { trpc } from 'src/trpc'

// Detach a circle from a shared base role by giving it its own independent copy
// (base: false) and repointing the circle to that new role. The base role and
// the other circles using it are left untouched.
//
// The copy and the repointing run server side (circle.separateFromBaseRole):
// writing circle.roleId is not open to Hasura writes, and the server reads the
// base role's content itself (#94, 24/09/2026).
export default function useSeparateFromBaseRole() {
  const createLog = useCreateLog()
  const { orgId, getOrgData } = useOrgContext()

  return useCallback(
    async (circleId: string, _baseRole: RoleFragment) => {
      if (!orgId) return
      const circle = getOrgData()?.getCircle(circleId)
      if (!circle) return
      const previousRoleId = circle.roleId

      const { role: newRole } = await trpc.circle.separateFromBaseRole.mutate({
        orgId,
        circleId,
      })

      // Logged for the history. Not undoable: undoing would write
      // circle.roleId, which only the server does (see OrgData.canCancelLog).
      createLog({
        display: {
          type: LogType.RoleCreate,
          id: newRole.id,
          name: newRole.name,
        },
        changes: {
          roles: [
            {
              type: EntityChangeType.Create,
              id: newRole.id,
              data: newRole,
            },
          ],
          circles: [
            {
              type: EntityChangeType.Update,
              id: circleId,
              prevData: { roleId: previousRoleId },
              newData: { roleId: newRole.id },
            },
          ],
        },
      })

      return newRole.id
    },
    [orgId, getOrgData, createLog]
  )
}
